'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Copy, RefreshCw, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomSqlGeneratorContent } from '../../data/random-sql-generator-content';
import Link from 'next/link';

type SqlQueryType = 'SELECT' | 'INSERT' | 'UPDATE' | 'DELETE' | 'CREATE_TABLE';

interface TableConfig {
    name: string;
    columns: {
        name: string;
        type: 'INTEGER' | 'VARCHAR' | 'TEXT' | 'BOOLEAN' | 'DATE' | 'TIMESTAMP';
        nullable: boolean;
        primaryKey: boolean;
    }[];
}

const DEFAULT_TABLE: TableConfig = {
    name: 'users',
    columns: [
        { name: 'id', type: 'INTEGER', nullable: false, primaryKey: true },
        { name: 'name', type: 'VARCHAR', nullable: false, primaryKey: false },
        { name: 'email', type: 'VARCHAR', nullable: false, primaryKey: false },
        { name: 'created_at', type: 'TIMESTAMP', nullable: false, primaryKey: false },
    ],
};

const generateRandomString = (length = 8) => {
    return Math.random().toString(36).substring(2, 2 + length);
};

const generateRandomEmail = () => {
    return `${generateRandomString(8)}@example.com`;
};

const generateRandomValue = (type: string) => {
    switch (type) {
        case 'INTEGER':
            return Math.floor(Math.random() * 1000);
        case 'VARCHAR':
            return `'${generateRandomString(8)}'`;
        case 'TEXT':
            return `'${generateRandomString(20)}'`;
        case 'BOOLEAN':
            return Math.random() > 0.5 ? 'TRUE' : 'FALSE';
        case 'DATE':
            const date = new Date();
            date.setDate(date.getDate() - Math.floor(Math.random() * 365));
            return `'${date.toISOString().split('T')[0]}'`;
        case 'TIMESTAMP':
            return `'${new Date().toISOString().replace('T', ' ').substring(0, 19)}'`;
        default:
            return 'NULL';
    }
};

const generateSelectQuery = (table: TableConfig, limit: number): string => {
    const columns = table.columns.map(col => col.name).join(', ');
    return `SELECT ${columns} FROM ${table.name}${limit > 0 ? ` LIMIT ${limit}` : ''};`;
};

const generateInsertQuery = (table: TableConfig, count: number): string => {
    const columns = table.columns.map(col => col.name).join(', ');
    const values = [];

    for (let i = 0; i < count; i++) {
        const rowValues = table.columns.map(col => {
            if (col.primaryKey && col.type === 'INTEGER') {
                return i + 1; // Auto-increment primary key
            }
            return generateRandomValue(col.type);
        }).join(', ');

        values.push(`(${rowValues})`);
    }

    return `INSERT INTO ${table.name} (${columns}) VALUES\n  ${values.join(',\n  ')};`;
};

const generateUpdateQuery = (table: TableConfig): string => {
    const pkColumn = table.columns.find(col => col.primaryKey) || table.columns[0];
    const updateColumns = table.columns
        .filter(col => !col.primaryKey)
        .map(col => `${col.name} = ${generateRandomValue(col.type)}`)
        .join(', ');

    return `UPDATE ${table.name}
SET ${updateColumns}
WHERE ${pkColumn.name} = ${generateRandomValue('INTEGER')};`;
};

const generateDeleteQuery = (table: TableConfig): string => {
    const pkColumn = table.columns.find(col => col.primaryKey) || table.columns[0];
    return `DELETE FROM ${table.name} WHERE ${pkColumn.name} = ${generateRandomValue('INTEGER')};`;
};

const generateCreateTableQuery = (table: TableConfig): string => {
    const columns = table.columns.map(col => {
        let columnDef = `${col.name} ${col.type}`;
        if (col.primaryKey) columnDef += ' PRIMARY KEY';
        if (!col.nullable) columnDef += ' NOT NULL';
        return `    ${columnDef}`;
    }).join(',\n');

    return `CREATE TABLE ${table.name} (\n${columns}\n);`;
};

export default function RandomSqlGenerator() {
    const [queryType, setQueryType] = useState<SqlQueryType>('SELECT');
    const [rowCount, setRowCount] = useState<number>(5);
    const [sqlQuery, setSqlQuery] = useState<string>('');
    const [copied, setCopied] = useState<boolean>(false);
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [tableConfig, setTableConfig] = useState<TableConfig>(DEFAULT_TABLE);
    const { toast } = useToast();
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const generateQuery = useCallback((force = false) => {
        // Don't regenerate if already generating
        if (isGenerating && !force) return;

        setIsGenerating(true);

        // Clear any existing timeout
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        // Debounce the generation to avoid rapid updates
        debounceTimeout.current = setTimeout(() => {
            try {
                let query = '';

                switch (queryType) {
                    case 'SELECT':
                        query = generateSelectQuery(tableConfig, rowCount);
                        break;
                    case 'INSERT':
                        query = generateInsertQuery(tableConfig, rowCount);
                        break;
                    case 'UPDATE':
                        query = generateUpdateQuery(tableConfig);
                        break;
                    case 'DELETE':
                        query = generateDeleteQuery(tableConfig);
                        break;
                    case 'CREATE_TABLE':
                        query = generateCreateTableQuery(tableConfig);
                        break;
                    default:
                        query = '-- Select a query type to generate';
                }

                setSqlQuery(query);
            } catch (error) {
                console.error('Error generating SQL:', error);
                toast({
                    title: 'Error',
                    description: 'Failed to generate SQL query. Please try again.',
                    variant: 'destructive',
                });
            } finally {
                setIsGenerating(false);
            }
        }, 300);

        // Cleanup function
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [queryType, rowCount, tableConfig, toast, isGenerating]);

    const handleCopy = useCallback(() => {
        if (!sqlQuery) return;

        navigator.clipboard.writeText(sqlQuery);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'SQL query has been copied to clipboard.',
        });

        setTimeout(() => setCopied(false), 2000);
    }, [sqlQuery, toast]);

    const handleRegenerate = useCallback(() => {
        generateQuery();
        toast({
            title: 'Success',
            description: 'New SQL query has been generated.',
        });
    }, [generateQuery, toast]);

    // Initial generation on mount
    useEffect(() => {
        // Only generate on initial mount
        const timer = setTimeout(() => {
            generateQuery();
        }, 100);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array means this runs once on mount

    return (
        <div className="container mx-auto py-8 px-4">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8 text-center">
                    <ToolPageIntro title="Random SQL Generator">
                Generate random SQL queries and data with our free online tool. Create test databases, sample queries, and mock SQL data for development and testing.
            </ToolPageIntro>
                </div>

                <div className="grid gap-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Configuration</CardTitle>
                            <CardDescription>Customize your SQL query</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="query-type">Query Type</Label>
                                    <Select
                                        value={queryType}
                                        onValueChange={(value) => setQueryType(value as SqlQueryType)}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select query type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="SELECT">SELECT</SelectItem>
                                            <SelectItem value="INSERT">INSERT</SelectItem>
                                            <SelectItem value="UPDATE">UPDATE</SelectItem>
                                            <SelectItem value="DELETE">DELETE</SelectItem>
                                            <SelectItem value="CREATE_TABLE">CREATE TABLE</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {(queryType === 'SELECT' || queryType === 'INSERT') && (
                                    <div className="space-y-2">
                                        <Label htmlFor="row-count">
                                            {queryType === 'SELECT' ? 'Row Limit' : 'Number of Rows'}
                                        </Label>
                                        <Input
                                            id="row-count"
                                            type="number"
                                            min="1"
                                            max="1000"
                                            value={rowCount}
                                            onChange={(e) => setRowCount(parseInt(e.target.value) || 1)}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="pt-2">
                                <div className="flex items-center space-x-2">
                                    <Button
                                        onClick={handleRegenerate}
                                        disabled={isGenerating}
                                    >
                                        {isGenerating ? (
                                            <>
                                                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw className="mr-2 h-4 w-4" />
                                                Regenerate
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex justify-between items-center">
                                <CardTitle>Generated SQL</CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!sqlQuery}
                                >
                                    {copied ? (
                                        <Check className="h-4 w-4 mr-2" />
                                    ) : (
                                        <Copy className="h-4 w-4 mr-2" />
                                    )}
                                    {copied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="relative">
                                <pre className="bg-muted/50 p-4 rounded-md overflow-x-auto text-sm">
                                    <code>{sqlQuery || '// Generate a query to see the result'}</code>
                                </pre>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="mt-8 text-center text-sm text-muted-foreground">
                    <p>
                        This tool generates sample SQL queries for testing and development purposes.
                        Always review and test queries before using them in production environments.
                    </p>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomSqlGeneratorContent.title}
                description={randomSqlGeneratorContent.description}
                content={randomSqlGeneratorContent.content}
                faqs={randomSqlGeneratorContent.faqs}
                relatedTools={randomSqlGeneratorContent.relatedTools}
            />
        </div>
    );
}
